/**
 * Founder 2026-10-29 (netalinissim.com): Security assumed every site has
 * a Cloudflare Worker or WordPress plugin. A script tag runs in visitors'
 * browsers, so it can neither count nor refuse server requests: "0 would
 * have been refused" was not a measurement and "Turn on" could do
 * nothing. On Shopify, Shopify answers these requests itself.
 *
 * One answer for every Security view: does a server-side install report
 * from this site?
 *
 *   'server'        a Worker or plugin has reported an install version
 *                   (or the blocking read names its source).
 *   'shopify_none'  Shopify store with no Worker in front of it.
 *   'script_none'   any other site with no Worker or plugin seen.
 *
 * Only Worker and plugin requests write an install version, so a null
 * version means none has reported yet. A just-deployed Worker reads as
 * "none" until its first request; the copy says so.
 */

import type { Site } from '@sill/types';

import { SHOPIFY_SAFE_WORKER_AVAILABLE } from '../onboarding/install-copy.js';

export type ServerInstall = 'server' | 'shopify_none' | 'script_none';

export function serverInstallOf(
  site: Pick<Site, 'detected_platform'>,
  signals: {
    readonly install_version?: string | null;
    readonly install_source?: 'worker' | 'plugin' | null;
  },
): ServerInstall {
  const seen = (typeof signals.install_version === 'string' && signals.install_version.length > 0)
    || signals.install_source === 'worker'
    || signals.install_source === 'plugin';
  if (seen) return 'server';
  return site.detected_platform === 'shopify' ? 'shopify_none' : 'script_none';
}

/** Settings → Install deep link (same form the Blocking card uses). */
export const INSTALL_HREF = '?view=settings&settings_tab=install';

/** Paths needs a Worker or plugin; one honest sentence per case. */
export function pathsNoServerCopy(kind: Exclude<ServerInstall, 'server'>): string {
  return kind === 'shopify_none'
    ? `Shopify answers these requests itself, so they never reach a server of yours. Refusing them needs the Cloudflare Worker in front of your store, ${SHOPIFY_SAFE_WORKER_AVAILABLE ? 'which you can set up in Settings › Install.' : 'coming soon for Shopify.'}`
    : 'Needs the Cloudflare Worker or WordPress plugin. A script tag runs in visitors’ browsers, so it can’t see or refuse these requests. If you just installed one, this updates after its first request.';
}

/** Blocking needs the Worker; Shopify cannot have one yet. */
export function blockingShopifyCopy(): string {
  return SHOPIFY_SAFE_WORKER_AVAILABLE
    ? 'Blocking on Shopify needs the Cloudflare Worker in front of your store.'
    : 'Blocking on Shopify needs the Cloudflare Worker in front of your store, coming soon for Shopify.';
}
